import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, EmptyState, ErrorState, Spinner } from "@/components/common";
import { ExperienceCard } from "@/components/ExperienceCard";
import { api, apiError } from "@/lib/api";

const Group = ({ title, items, empty }) => (
  <section className="space-y-4">
    <h2 className="font-display text-3xl">{title} <span className="text-muted-foreground">({items.length})</span></h2>
    {items.length ? <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{items.map((e, i) => <ExperienceCard key={e.id} exp={e} index={i} />)}</div> : <p className="text-sm text-muted-foreground">{empty}</p>}
  </section>
);

const Requests = ({ items, reload }) => {
  const withdraw = async (id) => { try { await api.post(`/requests/${id}/withdraw`); toast.success("Request withdrawn"); reload(); } catch (e) { toast.error(apiError(e)); } };
  if (!items.length) return <EmptyState title="No open requests." body="Requests you send and invitations you receive appear here." testId="requests-empty" />;
  return (
    <ul className="space-y-3" data-testid="my-requests-list">
      {items.map((r) => (
        <li key={r.id} className="surface flex flex-wrap items-center justify-between gap-4 p-5" data-testid={`my-request-${r.id}`}>
          <div>
            <p className="text-sm text-muted-foreground">{r.invited ? "Invitation" : "Your request"} · {r.kind === "cohost" ? "Co-host" : "Guest place"}</p>
            <p className="font-display text-2xl">{r.experience.title}</p>
          </div>
          <div className="flex gap-3">
            <Link to={`/experiences/${r.experience.id}`} className="btn-primary min-h-[44px]" data-testid={`open-request-${r.id}`}>{r.invited ? "Review invitation" : "View"}</Link>
            {!r.invited && <button type="button" className="btn-outline min-h-[44px]" onClick={() => withdraw(r.id)} data-testid={`withdraw-${r.id}`}>Withdraw</button>}
          </div>
        </li>
      ))}
    </ul>
  );
};

const Directory = () => {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/members", { params: { q } }).then(({ data }) => setList(data)).catch(() => setList([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-6">
      <label className="relative block max-w-md">
        <span className="sr-only">Search members</span>
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input className="field pl-11" placeholder="Search by name or city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="directory-search-input" />
      </label>
      {!list ? <Spinner /> : list.length === 0 ? <p className="text-muted-foreground">No members found.</p> : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="member-directory">
          {list.map((m) => (
            <li key={m.id}><Link to={`/members/${m.id}`} className="surface card-hover flex items-center gap-4 p-5" data-testid={`member-card-${m.id}`}>
              <Avatar person={m} size={56} />
              <div className="min-w-0"><p className="font-display truncate text-2xl">{m.name}</p><p className="truncate text-sm text-muted-foreground">{m.home_city}</p></div>
            </Link></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Trigger = ({ value, children }) => (
  <TabsTrigger value={value} data-testid={`circle-tab-${value}`} className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-0 text-[15px] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none">{children}</TabsTrigger>
);

export default function Circle() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { const r = await api.get("/experiences/mine"); setData(r.data); setError(""); } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="shell pb-24 pt-10" data-testid="circle-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow mb-3">My circle</p><h1 className="font-display text-5xl leading-[0.95] lg:text-6xl">Your plans and people.</h1></div>
        <Link to="/experiences/new" className="btn-primary" data-testid="circle-create-button"><Plus className="h-4 w-4" /> Create experience</Link>
      </div>
      {error ? <div className="mt-8"><ErrorState message={error} onRetry={load} /></div> : !data ? <Spinner /> : (
        <Tabs defaultValue="plans" className="mt-8">
          <TabsList className="h-auto w-full justify-start gap-8 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
            <Trigger value="plans">Experiences</Trigger>
            <Trigger value="requests">Requests{data.requests.length ? ` (${data.requests.length})` : ""}</Trigger>
            <Trigger value="saved">Saved</Trigger>
            <Trigger value="members">Members</Trigger>
          </TabsList>
          <TabsContent value="plans" className="space-y-12 pt-8">
            {!data.hosting.length && !data.cohosting.length && !data.guest.length ? (
              <EmptyState title="Your circle starts here." body="Create an experience or request to join one from Discover." testId="circle-empty"
                action={<Link to="/discover" className="btn-outline" data-testid="circle-discover-link">Go to Discover</Link>} />
            ) : (
              <>
                <Group title="Hosting" items={data.hosting} empty="You aren't leading any experiences yet." />
                <Group title="Co-hosting" items={data.cohosting} empty="You aren't co-hosting any experiences yet." />
                <Group title="Guest" items={data.guest} empty="You haven't joined any experiences as a guest." />
              </>
            )}
          </TabsContent>
          <TabsContent value="requests" className="pt-8"><Requests items={data.requests} reload={load} /></TabsContent>
          <TabsContent value="saved" className="pt-8">
            {data.saved.length ? <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.saved.map((e, i) => <ExperienceCard key={e.id} exp={e} index={i} />)}</div> : <EmptyState title="Nothing saved yet." body="Tap the heart on any experience to keep it here." testId="saved-empty" />}
          </TabsContent>
          <TabsContent value="members" className="pt-8"><Directory /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
